/**
 * Browser bootstrap for the SPA. This is the thin DOM layer that binds the
 * framework-agnostic view-model and socket client (see interview-view-model,
 * socket-client) to the HTML shell in index.html.
 *
 * It wires:
 *  - Cognito hosted sign-in (authorization code + PKCE) for pre-approved
 *    users; nothing loads until the user is signed in;
 *  - a browser WebSocket to the interview agent on AgentCore Runtime, opened
 *    with the user's access token;
 *  - text answers, and voice answers (tap the mic to talk, tap again to send)
 *    that Nova Sonic transcribes; prompts are spoken aloud for voice turns, or
 *    for every turn when "Read aloud" is on;
 *  - a POC debug panel (session info, the live form view, a message log, and
 *    dev controls).
 *
 * Runtime config (agent runtime ARN, Cognito ids and sign-in domain, locales,
 * forms) is fetched from /config.json, which the frontend stack writes at
 * deploy time, falling back to window.__APP_CONFIG__ for local development.
 */
import './styles.css';
import { AuthGate } from './auth-gate';
import { CognitoAuthProvider } from './cognito-auth';
import {
  FormState,
  InterviewSocketClient,
  ServerMessage,
  Socket,
  VoiceEvent,
  agentWebSocketUrl,
} from './socket-client';
import { InterviewViewModel } from './interview-view-model';
import { VoiceController } from './voice-controller';
import { BrowserMicrophone, PcmPlayer, SilenceDetector } from './audio';

interface FormOption {
  formId: string;
  version: string;
  name: string;
}

interface AppConfig {
  region: string;
  agentRuntimeArn: string;
  cognito: { userPoolId: string; clientId: string; domainUrl: string };
  supportedLocales: string[];
  /** Forms offered in the picker (written by the frontend stack). */
  forms?: FormOption[];
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

/** WebSocket close code the agent uses when a token is missing or expired. */
const CLOSE_UNAUTHENTICATED = 4401;

/** Browser WebSocket implementing the Socket port, with lifecycle callbacks. */
class BrowserSocket implements Socket {
  private ws: WebSocket | null = null;
  private messageHandler: ((data: string) => void) | null = null;

  constructor(
    private readonly onClose?: (code: number, reason: string) => void,
    private readonly onSockError?: () => void,
  ) {}

  connect(url: string, protocols?: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url, protocols);
      this.ws = ws;
      ws.onopen = () => resolve();
      ws.onerror = () => {
        this.onSockError?.();
        reject(new Error('WebSocket connection failed'));
      };
      ws.onclose = (ev) => this.onClose?.(ev.code, ev.reason);
      ws.onmessage = (ev) => this.messageHandler?.(String(ev.data));
    });
  }

  send(data: string): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return Promise.reject(new Error('not connected'));
    this.ws.send(data);
    return Promise.resolve();
  }

  onMessage(handler: (data: string) => void): void {
    this.messageHandler = handler;
  }

  close(): Promise<void> {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }
    return Promise.resolve();
  }
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node as T;
}

/** Shorten a value for compact display in the live form view. */
function fmtValue(v: string | number | boolean | null): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

/** Interview session id: also the AgentCore runtime session id (>= 33 chars). */
function newSessionId(): string {
  return `sess-${crypto.randomUUID()}`;
}

function render(vm: InterviewViewModel): void {
  const state = vm.get();
  const transcript = el<HTMLElement>('transcript');
  transcript.innerHTML = '';
  for (const entry of state.transcript) {
    const line = document.createElement('p');
    line.className = `turn turn-${entry.role}`;
    const who = document.createElement('strong');
    who.textContent = entry.role === 'assistant' ? 'Assistant: ' : 'You: ';
    line.appendChild(who);
    line.appendChild(document.createTextNode(entry.text));
    transcript.appendChild(line);
  }
  // The scrolling element is the <main id="app"> container, not the list.
  // Scroll after layout so the newest turn is fully in view.
  const scroller = el<HTMLElement>('app');
  requestAnimationFrame(() => {
    scroller.scrollTop = scroller.scrollHeight;
  });

  const recap = el<HTMLElement>('recap');
  if (state.recap) {
    recap.textContent = state.recap;
    recap.hidden = false;
  } else {
    recap.hidden = true;
  }

  const input = el<HTMLInputElement>('answer-input');
  input.placeholder = state.currentPrompt ?? 'Type your answer…';
  input.disabled = state.complete;
}

async function loadConfig(): Promise<AppConfig | null> {
  try {
    const res = await fetch('./config.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = (await res.json()) as AppConfig;
      if (cfg.agentRuntimeArn && cfg.cognito?.domainUrl) return cfg;
    }
  } catch {
    // ignore and fall back
  }
  const local = window.__APP_CONFIG__;
  return local?.agentRuntimeArn ? local : null;
}

async function main(): Promise<void> {
  // --- Elements ---
  const authGateSection = el<HTMLElement>('auth-gate');
  const form = el<HTMLFormElement>('answer-form');
  const input = el<HTMLInputElement>('answer-input');
  const localeSelect = el<HTMLSelectElement>('locale-select');
  const voiceToggle = el<HTMLButtonElement>('voice-toggle');
  const speakToggle = el<HTMLInputElement>('speak-toggle');
  const connStatus = el<HTMLElement>('conn-status');
  const userLabel = el<HTMLElement>('user-label');
  const signOutBtn = el<HTMLButtonElement>('sign-out');
  const shell = el<HTMLElement>('app-shell');
  const formSelect = el<HTMLSelectElement>('form-select');

  const banner = (text: string | null): void => {
    authGateSection.hidden = text === null;
    authGateSection.textContent = text ?? '';
  };

  const config = await loadConfig();
  if (!config) {
    banner('This site is not configured (config.json is missing).');
    return;
  }

  // --- Sign-in (pre-approved users only; MFA is enforced by Cognito) ---
  const redirectUri = `${window.location.origin}/`;
  const auth = new CognitoAuthProvider(
    { domainUrl: config.cognito.domainUrl, clientId: config.cognito.clientId, redirectUri },
    window.sessionStorage,
    {
      location: () => window.location.href,
      assign: (url) => window.location.assign(url),
      replaceUrl: (url) => window.history.replaceState(null, '', url),
      fetch: (url, init) => window.fetch(url, init),
    },
  );
  const gate = new AuthGate(auth);
  try {
    banner('Signing you in…');
    const session = await gate.requireSession();
    userLabel.textContent = session.username ?? session.cognitoSub;
    signOutBtn.hidden = false;
  } catch (err) {
    banner(`Sign-in failed: ${(err as Error).message}. Reload the page to try again.`);
    return;
  }
  signOutBtn.addEventListener('click', () => void auth.signOut());

  // Debug panel elements
  const dbg = {
    form: el<HTMLElement>('dbg-form'),
    session: el<HTMLElement>('dbg-session'),
    sub: el<HTMLElement>('dbg-sub'),
    locale: el<HTMLElement>('dbg-locale'),
    ws: el<HTMLElement>('dbg-ws'),
    formView: el<HTMLElement>('dbg-form-view'),
    progress: el<HTMLElement>('dbg-progress'),
    log: el<HTMLElement>('dbg-log'),
    formId: el<HTMLInputElement>('dbg-formid'),
    formVer: el<HTMLInputElement>('dbg-formver'),
    restart: el<HTMLButtonElement>('dbg-restart'),
    reconnect: el<HTMLButtonElement>('dbg-reconnect'),
    clearLog: el<HTMLButtonElement>('dbg-clear-log'),
    toggle: el<HTMLButtonElement>('debug-toggle'),
    body: el<HTMLElement>('debug-body'),
  };
  shell.classList.add('debug-open');

  // Form picker: populated from config.json. Selecting a form syncs the debug
  // Form ID/version inputs and starts a fresh session. With no catalog (local
  // dev fallback), the picker is hidden and the debug inputs drive the form.
  const forms = config.forms ?? [];
  if (forms.length === 0) {
    formSelect.hidden = true;
    (formSelect.previousElementSibling as HTMLElement | null)?.setAttribute('hidden', '');
  } else {
    for (const f of forms) {
      const opt = document.createElement('option');
      opt.value = `${f.formId}@${f.version}`;
      opt.textContent = f.name;
      formSelect.appendChild(opt);
    }
    dbg.formId.value = forms[0].formId;
    dbg.formVer.value = forms[0].version;
  }

  const setConn = (state: 'connecting' | 'online' | 'error', label: string): void => {
    connStatus.dataset.state = state;
    connStatus.textContent = label;
  };

  const log = (kind: 'in' | 'out' | 'err' | 'sys', text: string): void => {
    const ts = new Date().toLocaleTimeString();
    const row = document.createElement('div');
    row.className = `log-${kind}`;
    const tag = { in: '←', out: '→', err: '⚠', sys: '•' }[kind];
    row.textContent = `${ts} ${tag} ${text}`;
    dbg.log.appendChild(row);
    dbg.log.scrollTop = dbg.log.scrollHeight;
  };

  const endpoint = { region: config.region, runtimeArn: config.agentRuntimeArn };
  const player = new PcmPlayer();

  // --- Voice capture (level meter, timer, auto-stop, live words) ---
  const liveToggle = el<HTMLInputElement>('live-toggle');
  const LIVE_KEY = 'txhhsc.liveVoice';
  liveToggle.checked = window.localStorage.getItem(LIVE_KEY) !== 'off';
  liveToggle.addEventListener('change', () => {
    window.localStorage.setItem(LIVE_KEY, liveToggle.checked ? 'on' : 'off');
    log('sys', `live voice ${liveToggle.checked ? 'on' : 'off'}`);
  });
  const voiceUi = {
    status: el<HTMLElement>('voice-status'),
    level: el<HTMLElement>('voice-level'),
    timer: el<HTMLElement>('voice-timer'),
    msg: el<HTMLElement>('voice-msg'),
    ack: el<HTMLElement>('voice-ack'),
    heard: el<HTMLElement>('voice-heard'),
  };
  interface Capture {
    live: boolean;
    detector: SilenceDetector;
    startedAt: number;
    ticker: number;
    /** The agent ended the answer (live mode): don't send audio_end. */
    serverStopped: boolean;
    level: number;
  }
  let capture: Capture | null = null;

  const fmtClock = (ms: number): string => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const hideVoiceUi = (): void => {
    voiceUi.status.hidden = true;
    voiceUi.status.classList.remove('processing');
    voiceUi.heard.hidden = true;
    voiceUi.heard.textContent = '';
    voiceUi.ack.textContent = '';
  };

  /** Mic level callback (every few ms): meter + browser-side end detection. */
  const onMicLevel = (level: number): void => {
    const c = capture;
    if (!c) return;
    c.level = Math.max(level, c.level * 0.9);
    if (c.live) return; // in live mode the agent detects the end of the answer
    const state = c.detector.feed(level, performance.now());
    if (state === 'end' || state === 'max' || state === 'no_speech') {
      log('sys', `voice: ${state === 'end' ? 'pause detected' : state === 'max' ? 'time limit' : 'no speech heard'}`);
      void stopVoice('auto');
    }
  };

  const startVoice = async (): Promise<void> => {
    if (!client || !voice) return;
    player.unlock();
    player.stop(); // barge-in over a spoken prompt
    const live = liveToggle.checked;
    await client.startAudio({ ...turnFields(), speak: true, live });
    capture = {
      live,
      detector: new SilenceDetector(),
      startedAt: performance.now(),
      serverStopped: false,
      level: 0,
      ticker: window.setInterval(() => {
        const c = capture;
        if (!c) return;
        voiceUi.timer.textContent = fmtClock(performance.now() - c.startedAt);
        voiceUi.level.style.transform = `scaleX(${Math.min(1, c.level * 8).toFixed(3)})`;
        c.level *= 0.6;
      }, 100),
    };
    await voice.startSpeaking();
    vm.setModality('voice');
    voiceToggle.setAttribute('aria-pressed', 'true');
    voiceUi.status.classList.remove('processing');
    voiceUi.status.hidden = false;
    voiceUi.timer.textContent = '0:00';
    voiceUi.ack.textContent = '';
    voiceUi.heard.hidden = true;
    voiceUi.msg.textContent = live
      ? 'Listening (live). Stop talking for 2 seconds when you are done, or tap the mic.'
      : 'Listening. Stop talking for 2 seconds when you are done, or tap the mic.';
    log('out', `voice: listening (${live ? 'live' : 'buffered'})`);
  };

  /** End the capture: by tap, by browser silence detection, or by the agent. */
  const stopVoice = async (reason: 'tap' | 'auto' | 'server'): Promise<void> => {
    const c = capture;
    if (!c) return;
    capture = null;
    window.clearInterval(c.ticker);
    voiceToggle.setAttribute('aria-pressed', 'false');
    await voice?.stopSpeaking();
    if (!c.serverStopped) await client?.endAudio();
    voiceUi.status.classList.add('processing');
    voiceUi.msg.textContent = 'Got it. Working on your answer…';
    log('out', `voice: sent (${reason})`);
  };

  const onVoiceEvent = (m: VoiceEvent): void => {
    if (m.type === 'audio_ack') {
      voiceUi.ack.textContent = `Server received ${(m.ms / 1000).toFixed(0)} s`;
    } else if (m.type === 'transcript_partial') {
      voiceUi.heard.hidden = false;
      voiceUi.heard.textContent = `Heard: ${m.text}`;
    } else if (m.type === 'listening_stopped') {
      log('in', 'voice: end of answer detected');
      if (capture) {
        capture.serverStopped = true;
        void stopVoice('server');
      }
    }
  };

  // --- Per-session mutable state ---
  let vm = new InterviewViewModel(config.supportedLocales[0] ?? 'en');
  let client: InterviewSocketClient | null = null;
  let sessionId = '';
  let turnCounter = 0;
  let formState: FormState | null = null;
  let voice: VoiceController | null = null;
  /** The assistant's wording of each question asked this session, by slot key. */
  const askedBySlot = new Map<string, string>();
  const slotTooltip = (key: string, label: string): string => {
    const asked = askedBySlot.get(key);
    return asked ? `${label}\nAsked: ${asked}` : label;
  };

  const renderDebug = (): void => {
    dbg.form.textContent = formState
      ? `${formState.name} (${formState.formId} v${formState.formVersion})`
      : `${dbg.formId.value} v${dbg.formVer.value}`;
    dbg.session.textContent = sessionId || '—';
    const s = gate.current();
    dbg.sub.textContent = s ? `${s.username ?? ''} (${s.cognitoSub})` : '—';
    dbg.locale.textContent = vm.get().locale;

    if (!formState) {
      dbg.progress.textContent = '0/0';
      dbg.formView.innerHTML = '<p class="muted">Waiting for the first turn…</p>';
      return;
    }

    dbg.progress.textContent = `${formState.totals.filled}/${formState.totals.required}`;
    dbg.formView.innerHTML = '';
    for (const section of formState.sections) {
      const sec = document.createElement('div');
      sec.className = 'fv-section';
      const head = document.createElement('div');
      head.className = 'fv-section-head';
      head.innerHTML =
        `<span>${escapeHtml(section.name)}</span>` +
        (section.committed ? '<span class="fv-committed">✓ committed</span>' : '');
      sec.appendChild(head);

      for (const slot of section.slots) {
        const row = document.createElement('div');
        row.className = `fv-slot${slot.current ? ' fv-current' : ''}`;
        const hasValue = slot.value !== null && slot.value !== '';
        row.innerHTML =
          `<div class="fv-slot-main">` +
          `<span class="fv-dot ${escapeHtml(slot.state)}"></span>` +
          `<span class="fv-label" title="${escapeHtml(slotTooltip(slot.key, slot.label))}">${escapeHtml(slot.label)}${slot.required ? '' : ' <span class="fv-opt">(opt)</span>'}</span>` +
          `</div>` +
          `<div class="fv-slot-meta">` +
          (hasValue ? `<span class="fv-value">${escapeHtml(fmtValue(slot.value))}</span>` : '') +
          `<span class="slot-state ${escapeHtml(slot.state)}">${escapeHtml(slot.state.replace('_', ' '))}</span>` +
          `</div>`;
        sec.appendChild(row);
      }
      dbg.formView.appendChild(sec);
    }
  };

  const applyServer = (msg: ServerMessage): void => {
    vm.apply(msg);
    if (msg.formState) formState = msg.formState;
    // Remember how the assistant worded each question, for the debug tooltip.
    if (msg.type === 'prompt' && msg.formState?.currentSlotKey) {
      askedBySlot.set(msg.formState.currentSlotKey, msg.prompt);
    }
    if (msg.type === 'prompt') log('in', `prompt: ${msg.prompt}`);
    else if (msg.type === 'complete') log('in', 'complete');
    else if (msg.type === 'reprompt') log('in', `reprompt: ${msg.message}`);
    else if (msg.type === 'transcript') log('in', `heard: ${msg.text}`);
    // The reply to a spoken answer has arrived: clear the voice status.
    if (msg.type !== 'transcript' && !capture) hideVoiceUi();
    render(vm);
    renderDebug();
  };

  const turnFields = () => ({
    formId: dbg.formId.value,
    formVersion: dbg.formVer.value,
    sessionId,
    locale: vm.get().locale,
    turnId: `t${++turnCounter}`,
  });

  const sendTurn = async (utterance: string | null): Promise<void> => {
    if (!client) return;
    const payload = { ...turnFields(), utterance, speak: speakToggle.checked };
    try {
      try {
        await client.sendTurn(payload);
      } catch (err) {
        // The connection dropped since the last turn: reconnect to the same
        // session (the agent keeps its state in DynamoDB) and retry once.
        if (!/not (connected|authenticated)/.test((err as Error).message)) throw err;
        log('sys', 'connection lost; reconnecting before sending');
        await connect(false);
        if (!client) throw err;
        await client.sendTurn(payload);
      }
      log('out', utterance === null ? 'open interview' : `answer: ${utterance}`);
      banner(null);
    } catch (err) {
      const m = (err as Error).message;
      log('err', `send failed: ${m}`);
      banner(`Could not send: ${m}`);
    }
  };

  let reconnectTimer: number | null = null;
  let reconnectAttempts = 0;
  /** Reconnect after an unexpected close, with a short backoff. */
  const scheduleReconnect = (): void => {
    if (reconnectTimer !== null || reconnectAttempts >= 5) return;
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 10_000);
    reconnectAttempts += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      log('sys', `reconnecting (attempt ${reconnectAttempts})`);
      void connect(false);
    }, delay);
  };

  /** Open (or reopen) the agent connection for the current session id. */
  const connect = async (opening: boolean): Promise<void> => {
    await client?.close();
    const socket = new BrowserSocket(
      (code, reason) => {
        setConn('error', 'Disconnected');
        log('sys', `socket closed (${code}) ${reason || ''}`.trim());
        if (code === CLOSE_UNAUTHENTICATED) {
          banner('Your sign-in expired. Choose Reconnect to continue.');
        } else {
          scheduleReconnect();
        }
      },
      () => log('err', 'socket error'),
    );
    const c = new InterviewSocketClient(socket, gate, endpoint);
    client = c;
    c.onServerMessage(applyServer);
    c.onAudio((m) => {
      if (m.type === 'audio') {
        const bytes = Uint8Array.from(atob(m.data), (ch) => ch.charCodeAt(0));
        void voice?.playResponse(bytes);
      }
    });
    c.onVoiceEvent(onVoiceEvent);
    c.onError((reason, raw) => {
      log('err', `server: ${reason}`);
      log('err', raw);
      if (!capture) hideVoiceUi();
      banner(`The assistant could not process that turn: ${reason}`);
    });
    voice = new VoiceController(
      new BrowserMicrophone(onMicLevel),
      { sendAudioChunk: (chunk) => c.sendAudioChunk(chunk) },
      player,
      () => {
        voiceToggle.setAttribute('aria-pressed', 'false');
        vm.setModality('text');
      },
    );

    try {
      setConn('connecting', 'Connecting…');
      banner('Connecting to the assistant…');
      dbg.ws.textContent = agentWebSocketUrl(endpoint, sessionId).split('?')[0];
      await c.open(sessionId);
      reconnectAttempts = 0;
      banner(null);
      setConn('online', 'Online');
      log('sys', `connected (runtime session ${sessionId})`);
      renderDebug();
      if (opening) await sendTurn(null);
    } catch (err) {
      const m = (err as Error).message;
      banner(`Could not connect: ${m}`);
      setConn('error', 'Offline');
      log('err', `connect failed: ${m}`);
    }
    renderDebug();
  };

  const startSession = async (): Promise<void> => {
    if (capture) {
      window.clearInterval(capture.ticker);
      capture = null;
    }
    if (voice?.isCapturing()) await voice.stopSpeaking();
    hideVoiceUi();
    voiceToggle.setAttribute('aria-pressed', 'false');
    player.stop();
    vm = new InterviewViewModel(localeSelect.value || config.supportedLocales[0] || 'en');
    formState = null;
    askedBySlot.clear();
    turnCounter = 0;
    sessionId = newSessionId();
    render(vm);
    await connect(true);
  };

  // --- Event wiring ---
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    if (speakToggle.checked) player.unlock();
    vm.recordUserTurn(text);
    render(vm);
    input.value = '';
    void sendTurn(text);
  });

  localeSelect.addEventListener('change', () => {
    vm.setLocale(localeSelect.value);
    renderDebug();
  });

  // Mic button: tap to talk. The answer ends by itself after a 2-second pause
  // (detected in the browser, or by the agent in live mode); tapping again
  // finishes early.
  voiceToggle.addEventListener('click', () => {
    void (async () => {
      if (!client || !voice) return;
      try {
        if (!capture) await startVoice();
        else await stopVoice('tap');
      } catch (err) {
        const m = (err as Error).message;
        capture = null;
        voiceToggle.setAttribute('aria-pressed', 'false');
        hideVoiceUi();
        log('err', `voice failed: ${m}`);
        banner(`Voice input is unavailable: ${m}. You can keep typing.`);
      }
    })();
  });

  // Debug controls
  formSelect.addEventListener('change', () => {
    const [formId, version] = formSelect.value.split('@');
    dbg.formId.value = formId;
    dbg.formVer.value = version;
    log('sys', `switched to form ${formId} v${version}`);
    void startSession();
  });
  dbg.restart.addEventListener('click', () => {
    log('sys', 'new session');
    void startSession();
  });
  dbg.reconnect.addEventListener('click', () => {
    log('sys', 'reconnect (same session)');
    void connect(false);
  });
  dbg.clearLog.addEventListener('click', () => {
    dbg.log.innerHTML = '';
  });
  dbg.toggle.addEventListener('click', () => {
    const open = shell.classList.toggle('debug-open');
    dbg.toggle.textContent = open ? 'Hide' : 'Show';
    dbg.toggle.setAttribute('aria-expanded', String(open));
  });

  await startSession();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string;
  });
}

void main();
